import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IFreightCategoryEntityAttributes extends IModelAttributes {
	label: string;
	abbreviation: string;
	disabled: boolean;

	freightDetails: Array<Models.FreightDetailEntity | Models.IFreightDetailEntityAttributes>;
	freightPriceRanges: Array<Models.FreightPriceRangeEntity | Models.IFreightPriceRangeEntityAttributes>;
	freightTypes: Array<Models.FreightTypeEntity | Models.IFreightTypeEntityAttributes>;
}
@entity('FreightCategoryEntity', 'Freight Category')
export default class FreightCategoryEntity extends Model implements IFreightCategoryEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The display label for the freight category.
	 */
	@observable
	@attribute<FreightCategoryEntity, string>()
	public label: string;

	/**
	 * The abbreviated name for the freight category.
	 */
	@observable
	@attribute<FreightCategoryEntity, string>()
	public abbreviation: string;

	/**
	 * Whether or not this freight category is disabled.
	 */
	@observable
	@attribute<FreightCategoryEntity, boolean>()
	public disabled: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public freightDetails: Models.FreightDetailEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public freightPriceRanges: Models.FreightPriceRangeEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public freightTypes: Models.FreightTypeEntity[] = [];

	constructor(attributes?: Partial<IFreightCategoryEntityAttributes>) {
		super(attributes);

		this.freightDetails = [];
		this.freightTypes = [];
		this.freightPriceRanges = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IFreightCategoryEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.label !== undefined) {
				this.label = attributes.label;
			}
			if (attributes.abbreviation !== undefined) {
				this.abbreviation = attributes.abbreviation;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.freightDetails !== undefined && Array.isArray(attributes.freightDetails)) {
				if (!this.freightDetails) {
					this.freightDetails = [];
				}
				for (const model of attributes.freightDetails) {
					if (model instanceof Models.FreightDetailEntity) {
						this.freightDetails.push(model);
					} else {
						this.freightDetails.push(new Models.FreightDetailEntity(model));
					}
				}
			}
			if (attributes.freightPriceRanges !== undefined && Array.isArray(attributes.freightPriceRanges)) {
				if (!this.freightPriceRanges) {
					this.freightPriceRanges = [];
				}
				for (const model of attributes.freightPriceRanges) {
					if (model instanceof Models.FreightPriceRangeEntity) {
						this.freightPriceRanges.push(model);
					} else {
						this.freightPriceRanges.push(new Models.FreightPriceRangeEntity(model));
					}
				}
			}
			if (attributes.freightTypes !== undefined && Array.isArray(attributes.freightTypes)) {
				if (!this.freightTypes) {
					this.freightTypes = [];
				}
				for (const model of attributes.freightTypes) {
					if (model instanceof Models.FreightTypeEntity) {
						this.freightTypes.push(model);
					} else {
						this.freightTypes.push(new Models.FreightTypeEntity(model));
					}
				}
			}
		}
	}
}
